import {
  HosApiAccount,
  HosApiAccountDevice,
  HosApiAccountLocation,
  HosApiDeviceModel,
  HosApiEvolveAccount,
  HosApiMediaRoomAccount,
} from '../../types/apiTypes';
import { mapExperienceGroup } from '../ExperienceGroups/useExperienceGroups';
import { mapRoutingGroup } from '../RoutingGroups/useRoutingGroups';

export const mapAccount = (
  account: HosApiAccount | ApiResAccount,
  operator: OperatorContext
): Account => {
  let mappedAccount;
  operator.type === 'Hospitality'
    ? (mappedAccount = mapHosAccount(account as HosApiAccount))
    : (mappedAccount = mapResAccount(account as ApiResAccount));

  return mappedAccount;
};

const mapCloudDvr = (couldDvr: ApiCloudDvr): CloudDvr => ({
  active: couldDvr.Active,
  description: couldDvr.Description,
  id: couldDvr.ID,
  featureSubscriptionId: couldDvr.FeatureSubscriptionID,
  mediafirstId: couldDvr.MediaFirstID,
});

const mapEvolveAccount = (
  evolveAccount: HosApiEvolveAccount
): EvolveAccount => ({
  lateCheckout: evolveAccount.CanLateCheckout,
});

const mapFipsCode = (code: ApiFipsCode): FipsCode => ({
  active: code.Active,
  code: code.Code,
  county: code.CountyName,
});

const mapHosAccount = (account: HosApiAccount): Account => ({
  creditLimit: account.MediaRoomAccount
    ? account.MediaRoomAccount.CreditLimit
    : null,
  devices: mapLocations(account.Locations),
  evolve: account.EvolveAccount
    ? mapEvolveAccount(account.EvolveAccount)
    : undefined,
  floor: account.Floor || 0,
  id: account.RoomNumber,
  mediaroom: account.MediaRoomAccount
    ? mapMrAccount(account.MediaRoomAccount)
    : undefined,
  packages: account.MediaRoomAccount
    ? account.MediaRoomAccount.SubscriberGroupIDs
    : [],
  roomGroup: account.RoomGroupID,
  roomNumber: account.SupplementalRoomNumber,
  roomType: account.RoomTypeID,
  site: account.AccountSiteID,
  systems: account.SystemTypes,
  transport: account.TransportTypeID,
  type: 'Hospitality',
});

const mapHosDevice = (
  device: HosApiAccountDevice,
  location: string
): Device => ({
  assetTag: device.AssetTag,
  id: device.ID,
  isDvr: device.MediaRoomProfileResponse?.IsDVR,
  location: location,
  mac: device.MACAddress,
  model: mapHosDeviceModel(device.Model),
  serial: device.SerialNumber,
  status: device.MediaRoomProfileResponse?.Status ?? true,
});

const mapHosDeviceModel = (model: HosApiDeviceModel): DeviceModel => ({
  active: model.Active,
  canDVR: model.CanDVR,
  clientStoreValue: model.ClientStoreValue,
  clientVersion: model.ClientVersion,
  id: model.ID,
  name: model.Name,
  system: model.SystemType,
  type: model.DeviceType,
});

const mapIpBlock = (block: ApiResMediafirstIpBlock): MediafirstIpBlock => ({
  endIp: block.EndIP,
  isSingle: block.IsSingle,
  startIp: block.StartIP,
});

const mapLocations = (
  locations: HosApiAccountLocation[]
): HosAccountLocation[] => {
  return locations.map((location) => ({
    id: location.ID,
    devices: location.Devices.map((d) => mapHosDevice(d, location.Name)),
    name: location.Name,
  }));
};

const mapMrAccount = (
  mrAccount: HosApiMediaRoomAccount
): HosMediaroomAccount => ({
  dvrStatus: mrAccount.DVRStatus,
  lateCheckout: mrAccount.CanLateCheckout,
  status: mrAccount.Status,
  vod: mrAccount.VODPrivilegeID,
});

const mapResAccount = (account: ApiResAccount): Account => ({
  id: account.AccountNumber,
  creditLimit: account.CreditLimit,
  devices: account.Devices.map((d) => mapResDevice(d)) ?? [],
  mediafirst: account.MediaFirstAccount
    ? mapResMfAccount(account.MediaFirstAccount)
    : undefined,
  mediaroom: account.MediaRoomAccount
    ? mapResMrAccount(account.MediaRoomAccount)
    : undefined,
  packages: account.SubscriberGroups.map((sg) => sg.ID) ?? [],
  transport: account.TransportType,
  accountName: account.AccountName,
  fipsCode: account.FIPSCode ? mapFipsCode(account.FIPSCode) : null,
  phoneNumber: account.PhoneNumber,
  timeZoneName: account.TimeZoneName,
  type: 'Residential',
});

export const mapResDevice = (device: ApiResAccountDevice): Device => ({
  id: device.DeviceID,
  isDvr: device.IsDVR,
  location: device.Location,
  mac: device.MACAddress,
  model: mapResDeviceModel(device.DeviceModel),
  serial: device.SerialNumber,
  status: device.Status,
});

const mapResDeviceModel = (model: ApiResDeviceModel): DeviceModel => ({
  active: model.Active,
  clientStoreValue: model.ClientStoreValue,
  canDVR: model.CanDVR,
  name: model.Model,
  system: model.System,
  clientVersion: model.ClientVersion,
  type: model.System === 'MR' ? 'MediaRoom Set-Top Box' : null,
});

export const mapResMfAccount = (
  mfAccount: ApiResMediafirstAccount
): MediafirstAccount => ({
  cloudDvr: mfAccount.CloudDVR ? mapCloudDvr(mfAccount.CloudDVR) : null,
  cloudDvrStatus: mfAccount.CloudDVRStatus,
  experienceGroup: mapExperienceGroup(mfAccount.ExperienceGroup),
  ipAddress: mfAccount.IPAddress,
  ipBlock: mapIpBlock(mfAccount.IPBlock),
  lastTokenRequestTime: mfAccount.LastTokenRequestTime
    ? new Date(mfAccount.LastTokenRequestTime)
    : null,
  loginID: mfAccount.LoginID,
  reachClientUrl: mfAccount.ReachClientUrl,
  reachDeviceStreamCount: mfAccount.ReachDeviceStreamCount,
  registerUrl: mfAccount.RegisterUrl,
  routingGroup: mapRoutingGroup(mfAccount.RoutingGroup),
  status: mfAccount.Status,
  username: mfAccount.Username,
});

const mapResMrAccount = (
  mrAccount: ApiResMediaroomAccount
): ResMediaroomAccount => ({
  iccStatus: mrAccount.ICCStatus,
  dvrStatus: mrAccount.DVRStatus,
  status: mrAccount.Status,
  useScreensaver: mrAccount.UseScreensaver,
  wholeHome: mrAccount.WholeHome,
});

const mapResPackage = (
  subscriberGroup: ApiResSubscriberGroup
): SubscriberGroup => ({
  active: subscriberGroup.Active,
  description: subscriberGroup.Description,
  id: subscriberGroup.ID,
  name: subscriberGroup.Name,
  type: subscriberGroup.Base
    ? 'base'
    : subscriberGroup.AlaCarte
    ? 'alaCarte'
    : 'addons',
});
